/**
 * Utility functions to help password managers recognize 2FA authentication flows
 */
import { scheduleTask, batchTasks } from './performanceOptimizer';
import { logger } from "./logger";
// import { logger } from "./logger";
export const setupPasswordManagerSupport = (email: string) => {
    // Create a hidden form that password managers can detect
    const createHiddenForm = () => {
        const form = document.createElement('form');
        form.style.display = 'none';
        form.setAttribute('method', 'post');
        form.setAttribute('action', '/api/auth/2fa/verify');
        form.setAttribute('data-form-type', '2fa-verification');
        form.setAttribute('autoComplete', 'on');
        form.setAttribute('data-lpignore', 'false');
        form.setAttribute('data-1p-ignore', 'false');
        form.setAttribute('data-bitwarden-ignore', 'false');
        form.setAttribute('data-lastpass-ignore', 'false');
        // Apple Keychain specific form attributes
        form.setAttribute('data-apple-keychain', 'true');
        form.setAttribute('data-2fa-form', 'true');
        form.setAttribute('data-otp-form', 'true');
        form.setAttribute('data-verification-form', 'true');
        // Add hidden fields that password managers expect
        const emailInput = document.createElement('input');
        emailInput.type = 'text';
        emailInput.name = 'email';
        emailInput.value = email;
        emailInput.setAttribute('autoComplete', 'username');
        const usernameInput = document.createElement('input');
        usernameInput.type = 'text';
        usernameInput.name = 'username';
        usernameInput.value = email;
        usernameInput.setAttribute('autoComplete', 'username');
        const passwordInput = document.createElement('input');
        passwordInput.type = 'password';
        passwordInput.name = 'password';
        passwordInput.setAttribute('autoComplete', 'current-password');
        const actionInput = document.createElement('input');
        actionInput.type = 'hidden';
        actionInput.name = 'action';
        actionInput.value = '2fa-verification';
        // Add multiple OTP fields for different password manager naming conventions
        const otpFields = [
            { name: 'otp', type: 'text' },
            { name: 'totp', type: 'text' },
            { name: 'verification-code', type: 'text' },
            { name: 'code', type: 'text' },
            { name: '2fa-code', type: 'text' },
            { name: 'two-factor-code', type: 'text' },
            { name: 'twofa-code', type: 'text' },
        ];
        otpFields.forEach(field => {
            const input = document.createElement('input');
            input.type = field.type;
            input.name = field.name;
            input.setAttribute('autoComplete', 'one-time-code');
            input.setAttribute('data-lp-field-type', 'otp');
            input.setAttribute('data-1p-field-type', 'otp');
            input.setAttribute('data-bitwarden-field-type', 'otp');
            input.setAttribute('data-lastpass-field-type', 'otp');
            // Apple Keychain specific attributes
            input.setAttribute('data-apple-keychain', 'true');
            input.setAttribute('data-otp', 'true');
            input.setAttribute('data-totp', 'true');
            input.setAttribute('data-2fa', 'true');
            input.setAttribute('data-verification-code', 'true');
            input.setAttribute('data-field-type', 'otp');
            input.setAttribute('data-password-manager', 'supported');
            input.setAttribute('data-autofill', 'one-time-code');
            input.setAttribute('data-credential-type', 'otp');
            input.setAttribute('data-credential-field', 'verification-code');
            input.setAttribute('data-credential-purpose', '2fa');
            // Additional Apple Keychain specific attributes
            input.setAttribute('data-apple-keychain-otp', 'true');
            input.setAttribute('data-apple-keychain-2fa', 'true');
            input.setAttribute('data-apple-keychain-verification', 'true');
            input.setAttribute('data-apple-keychain-autofill', 'true');
            input.setAttribute('data-apple-keychain-code', 'true');
            input.setAttribute('data-apple-keychain-totp', 'true');
            input.setAttribute('data-apple-keychain-2fa-code', 'true');
            input.setAttribute('data-apple-keychain-verification-code', 'true');
            form.appendChild(input);
        });
        form.appendChild(emailInput);
        form.appendChild(usernameInput);
        form.appendChild(passwordInput);
        form.appendChild(actionInput);
        return form;
    };
    // Add the hidden form to the document
    const hiddenForm = createHiddenForm();
    document.body.appendChild(hiddenForm);
    // Optimized trigger password manager analysis with requestAnimationFrame
    const triggerPasswordManagerAnalysis = () => {
        const timers = [];
        // Use optimized scheduling for better performance
        const scheduleEvent = (callback: () => void, delay: number, taskId: string) => {
            return scheduleTask(callback, delay, taskId);
        };
        // Immediate trigger
        timers.push(scheduleEvent(() => {
            // Dispatch events that password managers listen for
            const events = ['focus', 'input', 'change', 'blur', 'keydown', 'keyup'];
            events.forEach(eventType => {
                hiddenForm.dispatchEvent(new Event(eventType, { bubbles: true }));
            });
        }, 50, 'password_manager_immediate'));
        // Short delay for password managers
        timers.push(scheduleEvent(() => {
            // Specific triggers for popular password managers
            if (window.lastpass) {
                window.lastpass.analyzeForm(hiddenForm);
            }
            if (window.OnePassword) {
                window.OnePassword.analyzeForm(hiddenForm);
            }
            if (window.bitwarden) {
                window.bitwarden.analyzeForm(hiddenForm);
            }
            // Additional triggers for password managers
            const inputs = document.querySelectorAll('input[autocomplete="one-time-code"]');
            inputs.forEach(input => {
                input.dispatchEvent(new Event('focus', { bubbles: true }));
                input.dispatchEvent(new Event('input', { bubbles: true }));
                input.dispatchEvent(new Event('change', { bubbles: true }));
            });
        }, 150, 'password_manager_short'));
        // Medium delay for slower password managers
        timers.push(scheduleEvent(() => {
            const otpInputs = document.querySelectorAll('input[name="verificationCode"], input[autocomplete="one-time-code"]');
            otpInputs.forEach(input => {
                input.dispatchEvent(new Event('focus', { bubbles: true }));
                input.dispatchEvent(new Event('click', { bubbles: true }));
                input.dispatchEvent(new Event('input', { bubbles: true }));
            });
        }, 300, 'password_manager_medium'));
        // Long delay for Apple Keychain specifically
        timers.push(scheduleEvent(() => {
            const appleKeychainInputs = document.querySelectorAll('input[data-apple-keychain="true"]');
            appleKeychainInputs.forEach(input => {
                input.dispatchEvent(new Event('focus', { bubbles: true }));
                input.dispatchEvent(new Event('input', { bubbles: true }));
                input.dispatchEvent(new Event('change', { bubbles: true }));
            });
        }, 800, 'password_manager_long'));
        // Additional delay for Apple Keychain
        timers.push(scheduleEvent(() => {
            const verificationInput = document.querySelector('input[name="verificationCode"]') as HTMLInputElement;
            if (verificationInput) {
                verificationInput.focus();
                verificationInput.click();
                verificationInput.dispatchEvent(new Event('focus', { bubbles: true }));
                verificationInput.dispatchEvent(new Event('input', { bubbles: true }));
                verificationInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }, 1200, 'password_manager_additional'));
        // Final trigger for Apple Keychain
        timers.push(scheduleEvent(() => {
            const form = document.querySelector('form[data-form-type="2fa-verification"]') as HTMLFormElement;
            if (form) {
                form.dispatchEvent(new Event('focus', { bubbles: true }));
                form.dispatchEvent(new Event('input', { bubbles: true }));
            }
        }, 1500, 'password_manager_final'));
        return timers;
    };
    // Clean up function
    const cleanup = () => {
        if (hiddenForm.parentNode) {
            hiddenForm.parentNode.removeChild(hiddenForm);
        }
    };
    return {
        triggerAnalysis: triggerPasswordManagerAnalysis,
        cleanup
    };
};
export const enhanceLoginForm = (formElement: HTMLFormElement | null) => {
    if (!formElement) {
        logger.warn('enhanceLoginForm: formElement is null or undefined');
        return;
    }
    // Add password manager specific attributes to the form
    const formAttributes = {
        'data-lpignore': 'false',
        'data-1p-ignore': 'false',
        'data-bitwarden-ignore': 'false',
        'data-lastpass-ignore': 'false',
        'data-password-manager': 'supported',
        'data-autofill': 'enabled',
        'data-credential-form': 'login',
        'data-form-type': 'login'
    };
    Object.entries(formAttributes).forEach(([key, value]) => {
        formElement.setAttribute(key, value);
    });
    // Enhance email/username input
    const emailInput = formElement.querySelector('input[name="email"], input[type="email"]') as HTMLInputElement;
    if (emailInput) {
        emailInput.setAttribute('data-lpignore', 'false');
        emailInput.setAttribute('data-1p-ignore', 'false');
        emailInput.setAttribute('data-bitwarden-ignore', 'false');
        emailInput.setAttribute('data-lastpass-ignore', 'false');
        emailInput.setAttribute('data-password-manager', 'supported');
        emailInput.setAttribute('data-autofill', 'username');
        emailInput.setAttribute('data-credential-type', 'username');
        emailInput.setAttribute('data-field-type', 'email');
    }
    // Enhance password input
    const passwordInput = formElement.querySelector('input[name="password"], input[type="password"]') as HTMLInputElement;
    if (passwordInput) {
        passwordInput.setAttribute('data-lpignore', 'false');
        passwordInput.setAttribute('data-1p-ignore', 'false');
        passwordInput.setAttribute('data-bitwarden-ignore', 'false');
        passwordInput.setAttribute('data-lastpass-ignore', 'false');
        passwordInput.setAttribute('data-password-manager', 'supported');
        passwordInput.setAttribute('data-autofill', 'current-password');
        passwordInput.setAttribute('data-credential-type', 'password');
        passwordInput.setAttribute('data-field-type', 'password');
    }
    // Enhance 2FA/OTP input fields for iCloud Keychain verification codes
    const otpInputs = formElement.querySelectorAll('input[name="verificationCode"], input[name="totp"], input[autocomplete="one-time-code"], input[data-login-field="one-time-code"]') as NodeListOf<HTMLInputElement>;
    otpInputs.forEach(otpInput => {
        // Standard password manager attributes
        otpInput.setAttribute('data-lpignore', 'false');
        otpInput.setAttribute('data-1p-ignore', 'false');
        otpInput.setAttribute('data-bitwarden-ignore', 'false');
        otpInput.setAttribute('data-lastpass-ignore', 'false');
        otpInput.setAttribute('data-password-manager', 'supported');
        otpInput.setAttribute('data-autofill', 'one-time-code');
        otpInput.setAttribute('data-credential-type', 'otp');
        otpInput.setAttribute('data-field-type', 'otp');
        // 2FA/OTP specific attributes
        otpInput.setAttribute('data-2fa-field', 'true');
        otpInput.setAttribute('data-totp-field', 'true');
        otpInput.setAttribute('data-otp-field', 'true');
        otpInput.setAttribute('data-verification-code', 'true');
        otpInput.setAttribute('data-mfa-code', 'true');
        otpInput.setAttribute('data-two-factor-code', 'true');
        // iCloud Keychain specific attributes
        otpInput.setAttribute('data-apple-keychain', 'true');
        otpInput.setAttribute('data-icloud-keychain', 'true');
        otpInput.setAttribute('data-apple-verification-code', 'true');
        otpInput.setAttribute('data-verification-code-field', 'true');
        otpInput.setAttribute('data-otp-field', 'true');
        otpInput.setAttribute('data-totp-field', 'true');
        // Ensure proper autocomplete attribute
        otpInput.setAttribute('autocomplete', 'one-time-code');
        // Add verification code specific attributes
        otpInput.setAttribute('data-verification-code-required', 'true');
        otpInput.setAttribute('data-2fa-required', 'true');
        otpInput.setAttribute('data-mfa-required', 'true');
    });
    // Add event listeners to trigger password manager analysis
    const inputs = formElement.querySelectorAll('input[type="email"], input[type="password"], input[name="verificationCode"], input[name="totp"], input[autocomplete="one-time-code"]');
    inputs.forEach(input => {
        const events = ['focus', 'input', 'change', 'blur'];
        events.forEach(eventType => {
            input.addEventListener(eventType, () => {
                // Trigger password manager analysis
                setTimeout(() => {
                    formElement.dispatchEvent(new Event('input', { bubbles: true }));
                }, 50);
            });
        });
    });
    // Create a hidden form for password managers to detect
    createHiddenLoginForm(formElement);
};
const createHiddenLoginForm = (originalForm: HTMLFormElement) => {
    // Check if hidden form already exists
    if (document.getElementById('hidden-login-form')) {
        return;
    }
    const hiddenForm = document.createElement('form');
    hiddenForm.id = 'hidden-login-form';
    hiddenForm.style.display = 'none';
    hiddenForm.setAttribute('method', 'post');
    // Detection-only form for password managers. Never post to the API — a native
    // submit would omit the CSRF header while still sending the session cookie
    // and trigger "CSRF token missing" on POST /api/auth/login.
    hiddenForm.setAttribute('action', '#');
    hiddenForm.setAttribute('data-form-type', 'login');
    hiddenForm.setAttribute('autoComplete', 'on');
    hiddenForm.setAttribute('data-lpignore', 'false');
    hiddenForm.setAttribute('data-1p-ignore', 'false');
    hiddenForm.setAttribute('data-bitwarden-ignore', 'false');
    hiddenForm.setAttribute('data-lastpass-ignore', 'false');
    hiddenForm.setAttribute('data-password-manager', 'supported');
    hiddenForm.setAttribute('data-autofill', 'enabled');
    hiddenForm.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
    });
    // Add hidden fields that password managers expect
    const emailInput = document.createElement('input');
    emailInput.type = 'email';
    emailInput.name = 'email';
    emailInput.setAttribute('autoComplete', 'email');
    emailInput.setAttribute('data-lpignore', 'false');
    emailInput.setAttribute('data-1p-ignore', 'false');
    emailInput.setAttribute('data-bitwarden-ignore', 'false');
    emailInput.setAttribute('data-lastpass-ignore', 'false');
    const usernameInput = document.createElement('input');
    usernameInput.type = 'text';
    usernameInput.name = 'username';
    usernameInput.setAttribute('autoComplete', 'username');
    usernameInput.setAttribute('data-lpignore', 'false');
    usernameInput.setAttribute('data-1p-ignore', 'false');
    usernameInput.setAttribute('data-bitwarden-ignore', 'false');
    usernameInput.setAttribute('data-lastpass-ignore', 'false');
    const passwordInput = document.createElement('input');
    passwordInput.type = 'password';
    passwordInput.name = 'password';
    passwordInput.setAttribute('autoComplete', 'current-password');
    passwordInput.setAttribute('data-lpignore', 'false');
    passwordInput.setAttribute('data-1p-ignore', 'false');
    passwordInput.setAttribute('data-bitwarden-ignore', 'false');
    passwordInput.setAttribute('data-lastpass-ignore', 'false');
    hiddenForm.appendChild(emailInput);
    hiddenForm.appendChild(usernameInput);
    hiddenForm.appendChild(passwordInput);
    // Add to document
    document.body.appendChild(hiddenForm);
    // Sync values from visible form to hidden form
    const syncValues = () => {
        const visibleEmail = originalForm.querySelector('input[name="email"]') as HTMLInputElement;
        const visiblePassword = originalForm.querySelector('input[name="password"]') as HTMLInputElement;
        if (visibleEmail) {
            emailInput.value = visibleEmail.value;
            usernameInput.value = visibleEmail.value;
        }
        if (visiblePassword) {
            passwordInput.value = visiblePassword.value;
        }
    };
    // Sync on input changes
    originalForm.addEventListener('input', syncValues);
    originalForm.addEventListener('change', syncValues);
};
export const enhance2FAInput = (inputElement: HTMLInputElement) => {
    // Add additional attributes for better password manager recognition
    const attributes = {
        'data-lpignore': 'false',
        'data-1p-ignore': 'false',
        'data-bitwarden-widget': '2fa-code',
        'data-lastpass-ignore': 'false',
        'data-form-type': '2fa',
        'data-autocomplete': 'one-time-code',
        'data-inputmode': 'numeric',
        'data-pattern': '[0-9]*',
        // Apple Keychain specific attributes
        'data-apple-keychain': 'true',
        'data-otp': 'true',
        'data-totp': 'true',
        'data-2fa': 'true',
        'data-verification-code': 'true',
        'data-field-type': 'otp',
        'data-password-manager': 'supported',
        'data-autofill': 'one-time-code',
        'data-credential-type': 'otp',
        'data-credential-field': 'verification-code',
        'data-credential-purpose': '2fa',
        // Additional Apple Keychain specific attributes
        'data-apple-keychain-otp': 'true',
        'data-apple-keychain-2fa': 'true',
        'data-apple-keychain-verification': 'true',
        'data-apple-keychain-autofill': 'true',
        'data-apple-keychain-code': 'true',
        'data-apple-keychain-totp': 'true',
        'data-apple-keychain-2fa-code': 'true',
        'data-apple-keychain-verification-code': 'true',
    };
    Object.entries(attributes).forEach(([key, value]) => {
        inputElement.setAttribute(key, value);
    });
    // Add event listeners for password manager triggers
    const events = ['focus', 'input', 'change', 'blur'];
    events.forEach(eventType => {
        inputElement.addEventListener(eventType, () => {
            // Trigger password manager analysis on input changes
            setTimeout(() => {
                const form = inputElement.closest('form');
                if (form) {
                    form.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }, 50);
        });
    });
};
export const enhanceTOTPInput = (inputElement: HTMLInputElement, shouldFocus: boolean = true) => {
    // First apply the standard 2FA input enhancements
    enhance2FAInput(inputElement);
    // If shouldFocus is true, focus the input and trigger events
    if (shouldFocus) {
        // Use setTimeout to ensure the attributes are applied first
        setTimeout(() => {
            inputElement.focus();
            // Trigger events that password managers listen for
            inputElement.dispatchEvent(new Event('focus', { bubbles: true }));
            inputElement.dispatchEvent(new Event('input', { bubbles: true }));
            inputElement.dispatchEvent(new Event('change', { bubbles: true }));
        }, 50);
    }
};
export const enhanceEmailForm = (formElement: HTMLFormElement | null) => {
    if (!formElement) {
        logger.warn('enhanceEmailForm: formElement is null or undefined');
        return;
    }
    // Add password manager specific attributes to the form
    const formAttributes = {
        'data-lpignore': 'false',
        'data-1p-ignore': 'false',
        'data-bitwarden-ignore': 'false',
        'data-lastpass-ignore': 'false',
        'data-password-manager': 'supported',
        'data-autofill': 'enabled',
        'data-credential-form': 'email',
        'data-form-type': 'email'
    };
    Object.entries(formAttributes).forEach(([key, value]) => {
        formElement.setAttribute(key, value);
    });
    // Enhance email input
    const emailInput = formElement.querySelector('input[name="email"], input[type="email"]') as HTMLInputElement;
    if (emailInput) {
        emailInput.setAttribute('data-lpignore', 'false');
        emailInput.setAttribute('data-1p-ignore', 'false');
        emailInput.setAttribute('data-bitwarden-ignore', 'false');
        emailInput.setAttribute('data-lastpass-ignore', 'false');
        emailInput.setAttribute('data-password-manager', 'supported');
        emailInput.setAttribute('data-autofill', 'username');
        emailInput.setAttribute('data-credential-type', 'username');
        emailInput.setAttribute('data-field-type', 'email');
    }
    // Add event listeners to trigger password manager analysis
    const inputs = formElement.querySelectorAll('input[type="email"]');
    inputs.forEach(input => {
        const events = ['focus', 'input', 'change', 'blur'];
        events.forEach(eventType => {
            input.addEventListener(eventType, () => {
                // Trigger password manager analysis
                setTimeout(() => {
                    formElement.dispatchEvent(new Event('input', { bubbles: true }));
                }, 50);
            });
        });
    });
};
export const enhancePasswordResetForm = (formElement: HTMLFormElement | null) => {
    if (!formElement) {
        logger.warn('enhancePasswordResetForm: formElement is null or undefined');
        return;
    }
    // Add password manager specific attributes to the form
    const formAttributes = {
        'data-lpignore': 'false',
        'data-1p-ignore': 'false',
        'data-bitwarden-ignore': 'false',
        'data-lastpass-ignore': 'false',
        'data-password-manager': 'supported',
        'data-autofill': 'enabled',
        'data-credential-form': 'password-reset',
        'data-form-type': 'password-reset'
    };
    Object.entries(formAttributes).forEach(([key, value]) => {
        formElement.setAttribute(key, value);
    });
    // Enhance email input
    const emailInput = formElement.querySelector('input[name="email"], input[type="email"]') as HTMLInputElement;
    if (emailInput) {
        emailInput.setAttribute('data-lpignore', 'false');
        emailInput.setAttribute('data-1p-ignore', 'false');
        emailInput.setAttribute('data-bitwarden-ignore', 'false');
        emailInput.setAttribute('data-lastpass-ignore', 'false');
        emailInput.setAttribute('data-password-manager', 'supported');
        emailInput.setAttribute('data-autofill', 'username');
        emailInput.setAttribute('data-credential-type', 'username');
        emailInput.setAttribute('data-field-type', 'email');
    }
    // Enhance password inputs
    const passwordInputs = formElement.querySelectorAll('input[type="password"], input[name="password"], input[name="newPassword"], input[name="confirmPassword"]') as NodeListOf<HTMLInputElement>;
    passwordInputs.forEach(passwordInput => {
        passwordInput.setAttribute('data-lpignore', 'false');
        passwordInput.setAttribute('data-1p-ignore', 'false');
        passwordInput.setAttribute('data-bitwarden-ignore', 'false');
        passwordInput.setAttribute('data-lastpass-ignore', 'false');
        passwordInput.setAttribute('data-password-manager', 'supported');
        passwordInput.setAttribute('data-autofill', 'new-password');
        passwordInput.setAttribute('data-credential-type', 'password');
        passwordInput.setAttribute('data-field-type', 'password');
    });
    // Add event listeners to trigger password manager analysis
    const inputs = formElement.querySelectorAll('input[type="email"], input[type="password"]');
    inputs.forEach(input => {
        const events = ['focus', 'input', 'change', 'blur'];
        events.forEach(eventType => {
            input.addEventListener(eventType, () => {
                // Trigger password manager analysis
                setTimeout(() => {
                    formElement.dispatchEvent(new Event('input', { bubbles: true }));
                }, 50);
            });
        });
    });
};
export const enhancePasswordChangeForm = (formElement: HTMLFormElement | null) => {
    if (!formElement) {
        logger.warn('enhancePasswordChangeForm: formElement is null or undefined');
        return;
    }
    // Add password manager specific attributes to the form
    const formAttributes = {
        'data-lpignore': 'false',
        'data-1p-ignore': 'false',
        'data-bitwarden-ignore': 'false',
        'data-lastpass-ignore': 'false',
        'data-password-manager': 'supported',
        'data-autofill': 'enabled',
        'data-credential-form': 'password-change',
        'data-form-type': 'password-change'
    };
    Object.entries(formAttributes).forEach(([key, value]) => {
        formElement.setAttribute(key, value);
    });
    // Enhance current password input
    const currentPasswordInput = formElement.querySelector('input[name="currentPassword"], input[name="oldPassword"]') as HTMLInputElement;
    if (currentPasswordInput) {
        currentPasswordInput.setAttribute('data-lpignore', 'false');
        currentPasswordInput.setAttribute('data-1p-ignore', 'false');
        currentPasswordInput.setAttribute('data-bitwarden-ignore', 'false');
        currentPasswordInput.setAttribute('data-lastpass-ignore', 'false');
        currentPasswordInput.setAttribute('data-password-manager', 'supported');
        currentPasswordInput.setAttribute('data-autofill', 'current-password');
        currentPasswordInput.setAttribute('data-credential-type', 'password');
        currentPasswordInput.setAttribute('data-field-type', 'current-password');
    }
    // Enhance new password inputs
    const newPasswordInputs = formElement.querySelectorAll('input[name="newPassword"], input[name="confirmPassword"]') as NodeListOf<HTMLInputElement>;
    newPasswordInputs.forEach(passwordInput => {
        passwordInput.setAttribute('data-lpignore', 'false');
        passwordInput.setAttribute('data-1p-ignore', 'false');
        passwordInput.setAttribute('data-bitwarden-ignore', 'false');
        passwordInput.setAttribute('data-lastpass-ignore', 'false');
        passwordInput.setAttribute('data-password-manager', 'supported');
        passwordInput.setAttribute('data-autofill', 'new-password');
        passwordInput.setAttribute('data-credential-type', 'password');
        passwordInput.setAttribute('data-field-type', 'new-password');
    });
    // Add event listeners to trigger password manager analysis
    const inputs = formElement.querySelectorAll('input[type="password"]');
    inputs.forEach(input => {
        const events = ['focus', 'input', 'change', 'blur'];
        events.forEach(eventType => {
            input.addEventListener(eventType, () => {
                // Trigger password manager analysis
                setTimeout(() => {
                    formElement.dispatchEvent(new Event('input', { bubbles: true }));
                }, 50);
            });
        });
    });
};
// Type declarations for password manager APIs
declare global {
    interface Window {
        lastpass?: {
            analyzeForm: (form: HTMLFormElement) => void;
        };
        OnePassword?: {
            analyzeForm: (form: HTMLFormElement) => void;
        };
        bitwarden?: {
            analyzeForm: (form: HTMLFormElement) => void;
        };
    }
}
